import { Col, Row } from "antd";
import React, { ReactElement, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  DocumentCategories,
  IProjectAttachment,
  IProjectRouteParams,
} from "src/pages/projects/project/interfaces";
import { AttachmentType } from "src/constants";
import MobileDrawer from "src/components/mobile-drawer";
import { useParams } from "react-router-dom";
import { IDeleteSelectedFile } from "src/components/file-upload/interfaces";
import FileUploader from "src/components/file-upload";
import { getIsMobile } from "@songtradr/spa-common";
import { cloneDeep } from "lodash";
import useAuth from "src/auth/use-auth";
import { checkFilesCurrentlyUploading } from "src/components/file-upload/helpers";
import style from "../../../../styles";
import { IProjectUploadFile } from "../../../details/components/interfaces";
import { DocumentsTableColumns, DocumentsTableData } from "../../table-data";
import { FileDrawerTypes, generateDrawer } from "../mobile-drawers";

interface IProps {
  attachments: IProjectAttachment[];
}

const Estimates = ({ attachments }: IProps): ReactElement => {
  const { t } = useTranslation();

  const { getAccessToken, organisationId } = useAuth();
  const [accessToken, setAccessToken] = useState("");

  const [estimatesFileList, setEstimatesFileList] = useState<
    IProjectUploadFile[]
  >([]);

  const [
    selectedFileToDelete,
    setSelectedFileToDelete,
  ] = useState<IDeleteSelectedFile>();

  const [activeFile, setActiveFile] = useState<IProjectUploadFile>();

  const [showFileUploadDeleteModal, setShowFileUploadDeleteModal] = useState(
    false
  );

  const [isDrawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [activeDrawer, setActiveDrawer] = useState<FileDrawerTypes>(
    FileDrawerTypes.FileInfoDrawer
  );

  const isMobile = getIsMobile();
  const { id: projectId } = useParams<IProjectRouteParams>();

  const handleSetEstimatesFileList = useCallback(
    (files: IProjectUploadFile[]) => {
      setEstimatesFileList(files);
    },
    []
  );
  const handleSetActiveDrawer = useCallback((drawerType: FileDrawerTypes) => {
    setActiveDrawer(drawerType);
  }, []);
  const handleSetSelectedFileToDelete = useCallback(
    (file: IDeleteSelectedFile | undefined) => {
      setSelectedFileToDelete(file);
    },
    []
  );
  const handleShowFileUploadDeleteModal = useCallback((show: boolean) => {
    setShowFileUploadDeleteModal(show);
  }, []);
  const handleSetActiveFile = useCallback(
    (file: IProjectUploadFile | undefined) => {
      setActiveFile(file);
    },
    []
  );
  const toggleDrawer = useCallback(() => {
    setDrawerVisible((state) => !state);
  }, []);

  useEffect(() => {
    if (!checkFilesCurrentlyUploading(estimatesFileList)) {
      setEstimatesFileList(cloneDeep(attachments));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  useEffect(() => {
    void (() => {
      const userAccessToken: string = getAccessToken();
      setAccessToken(userAccessToken);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Row role="row" gutter={[8, 8]}>
        <Col span={24}>
          <div css={style.documentsUploadContainer}>
            <FileUploader
              attachmentType={AttachmentType.Documents}
              documentCategory={DocumentCategories.Estimate}
              invalidFileSizeErrorMessage={t(
                "File size exceeds maximum limit of 2GB"
              )}
              existingFiles={estimatesFileList}
              setFileList={handleSetEstimatesFileList}
              tableData={DocumentsTableData({
                filesList: estimatesFileList,
                accessToken,
                projectId,
                organisationId,
                setShowModal: handleShowFileUploadDeleteModal,
                setSelectedFileToDelete: handleSetSelectedFileToDelete,
                toggleDrawer,
                setActiveDrawer: handleSetActiveDrawer,
                setActiveFile: handleSetActiveFile,
              })}
              selectedFileToDelete={selectedFileToDelete}
              tableColumns={DocumentsTableColumns(isMobile, t)}
              showModal={showFileUploadDeleteModal}
              setShowModal={handleShowFileUploadDeleteModal}
              additionalContainerStyles={style.documentsFileUploader}
            />
          </div>
        </Col>
      </Row>
      {isMobile && (
        <MobileDrawer
          placement="bottom"
          onClose={toggleDrawer}
          open={isDrawerVisible}
          key="bottom"
          closable={false}
          data-testid="mobile-drawer-container"
          className="documents-drawer-container"
        >
          {generateDrawer(
            activeDrawer,
            handleSetActiveDrawer,
            toggleDrawer,
            accessToken,
            projectId,
            organisationId,
            handleSetEstimatesFileList,
            estimatesFileList,
            handleShowFileUploadDeleteModal,
            selectedFileToDelete,
            activeFile
          )}
        </MobileDrawer>
      )}
    </div>
  );
};

export default Estimates;
